// D1Page.jsx — Flash Fluency homepage (editorial direction, v6).
//
// WHAT CHANGED FROM PREVIOUS VERSIONS:
//   - Killed all italic emphasis (Fraunces italic was too swoopy)
//   - Killed the orange highlighter background bands (clipped descenders,
//     looked like broken underlines)
//   - Replaced with "style B" emphasis: one key word in orange per headline,
//     everything else in calm warm off-white serif. No tricks.
//   - Restructured Why Flash Fluency from 4 stacked deep-dives into a 3×2
//     grid of feature cards. Each card is self-contained (eyebrow, small
//     serif title, phone screenshot, chips). Solves the dead-space problem
//     at wide viewports.
//   - Added a "Conversation Practice (coming soon)" cell as the 6th grid
//     item, alongside Content-to-Curriculum.
//
// LAYOUT:
//   Nav → Hero → Why (3×2 grid) → Path → Stats → Pricing → CTA → Footer

import React from 'react';

import { D1, STORE_URLS, SOCIAL, isBeta } from './d1-tokens';
import D1Pricing from './D1Pricing';

import logo                     from './images/logo.png';
import pronScreenshot           from './images/screenshot_pronunciation.png';
import outputScreenshot         from './images/screenshot_translation.png';
import srsScreenshot            from './images/screenshot_srs.png';
import inputScreenshot          from './images/screenshot_input.png';
import personalizationScreenshot from './images/screenshot_personalization.png';

export { D1, STORE_URLS, SOCIAL };
export { FF_ENV, APP_URL } from './d1-tokens';

// ═══════════════════════════════════════════════════════════════════
// GLOBAL STYLES
// ═══════════════════════════════════════════════════════════════════

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@400;500;600&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&family=Noto+Serif+KR:wght@400;500;600&display=swap');

  .d1 *, .d1 *::before, .d1 *::after { box-sizing: border-box; }

  .d1 {
    --pad-x: 56px;
    background: ${D1.bg};
    color: ${D1.ink};
    font-family: ${D1.sans};
    overflow-x: hidden;
  }

  /* Serif display type — Fraunces roman only. No italics (the italic
   * letterforms are too swoopy for this design). */
  .d1-serif { font-family: ${D1.serif}; font-weight: 500; letter-spacing: -0.025em; line-height: 1.05; }
  .d1-ko    { font-family: ${D1.ko}; font-weight: 500; }
  .d1-eyebrow {
    font-family: ${D1.mono}; font-size: 12px;
    letter-spacing: 0.12em; text-transform: uppercase;
    color: ${D1.dim};
  }

  /* The ONE emphasis we use: key words in orange. No highlighters, no
   * italics, no background bands. Just a color shift on a single word or
   * short phrase per headline. */
  .d1-o { color: ${D1.orange}; }

  .d1-btn-primary {
    display: inline-flex; align-items: center; gap: 10px;
    background: ${D1.orange}; color: #1a1210;
    padding: 14px 22px; border-radius: 4px;
    font-family: ${D1.sans}; font-weight: 600; font-size: 15px;
    text-decoration: none;
    transition: background 120ms ease, transform 120ms ease;
  }
  .d1-btn-primary:hover  { background: ${D1.orangeDeep}; }
  .d1-btn-primary:active { transform: translateY(1px); }

  .d1-btn-ghost {
    display: inline-flex; align-items: center; gap: 10px;
    background: transparent; color: ${D1.ink};
    padding: 14px 22px; border: 1px solid ${D1.rule}; border-radius: 4px;
    font-weight: 500; font-size: 15px; text-decoration: none;
    transition: border-color 120ms ease, background 120ms ease;
  }
  .d1-btn-ghost:hover { border-color: rgba(245,239,230,0.2); background: rgba(245,239,230,0.02); }

  .d1-chip {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 6px 12px; border: 1px solid ${D1.rule}; border-radius: 999px;
    background: rgba(255,138,61,0.06);
    font-family: ${D1.mono}; font-size: 11px;
    letter-spacing: 0.1em; text-transform: uppercase;
    color: ${D1.orange};
  }
  .d1-chip-dot {
    width: 6px; height: 6px; border-radius: 999px;
    background: ${D1.orange};
    box-shadow: 0 0 8px ${D1.orange};
    animation: d1-pulse 2.4s ease-in-out infinite;
  }
  @keyframes d1-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }

  .d1-nav-link { color: ${D1.dim}; text-decoration: none; transition: color 120ms ease; }
  .d1-nav-link:hover { color: ${D1.ink}; }

  .d1-phone {
    border: 5px solid #0a0706;
    background: #000;
    border-radius: 28px;
    overflow: hidden;
    box-shadow: 0 20px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.04);
  }
  .d1-phone img { width: 100%; height: 100%; object-fit: cover; display: block; }

  .d1-deco-ko {
    font-family: ${D1.ko}; font-weight: 400;
    color: rgba(255,138,61,0.10);
    line-height: 0.85; letter-spacing: -0.05em;
    user-select: none; pointer-events: none;
  }

  .d1-coming {
    font-size: 10px; font-family: ${D1.mono}; font-weight: 700;
    color: ${D1.orange};
    letter-spacing: 0.08em; text-transform: uppercase;
    padding: 3px 9px;
    border: 1px solid ${D1.orange}; border-radius: 999px;
    background: rgba(255,138,61,0.08);
  }

  @media (max-width: 1279px) { .d1 { --pad-x: 40px; } }
  @media (max-width: 900px)  { .d1 { --pad-x: 28px; } .d1-nav-links { display: none !important; } }
  @media (max-width: 560px)  {
    .d1 { --pad-x: 20px; }
    .d1-btn-primary, .d1-btn-ghost { padding: 12px 18px; font-size: 14px; }
  }
`;

// ═══════════════════════════════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════════════════════════════

function StoreBadgeApple({ compact = false }) {
  return (
    <a href={STORE_URLS.appStore} target="_blank" rel="noopener noreferrer" style={sbS.a}>
      <svg width={compact ? 20 : 22} height={compact ? 20 : 22} viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
      </svg>
      {compact ? <span style={{ fontSize: 15, fontWeight: 500 }}>App Store</span> : (
        <div style={{ textAlign: 'left', lineHeight: 1.1 }}>
          <div style={{ fontSize: 10, opacity: 0.75 }}>Download on the</div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>App Store</div>
        </div>
      )}
    </a>
  );
}
function StoreBadgeGoogle({ compact = false }) {
  return (
    <a href={STORE_URLS.googlePlay} target="_blank" rel="noopener noreferrer" style={sbS.a}>
      <svg width={compact ? 20 : 22} height={compact ? 20 : 22} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 20.5V3.5l10 8.5L3 20.5z" fill={D1.orange}/>
        <path d="M13 12l3.5-3L21 11l-4.5 2.5L13 12z" fill="#fff"/>
      </svg>
      {compact ? <span style={{ fontSize: 15, fontWeight: 500 }}>Google Play</span> : (
        <div style={{ textAlign: 'left', lineHeight: 1.1 }}>
          <div style={{ fontSize: 10, opacity: 0.75 }}>Get it on</div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Google Play</div>
        </div>
      )}
    </a>
  );
}
const sbS = {
  a: { display: 'inline-flex', alignItems: 'center', gap: 10,
       background: '#000', color: '#fff', padding: '11px 18px',
       borderRadius: 8, textDecoration: 'none', border: `1px solid ${D1.rule}` },
};

function LogoMark({ size = 34 }) {
  return (
    <img
      src={logo}
      alt=""
      width={size}
      height={size}
      style={{
        display: 'block',
        borderRadius: size / 4,
        filter: `drop-shadow(0 0 ${size * 0.4}px ${D1.orange}66)`,
      }}
    />
  );
}

function FeatureChip({ title, body, comingSoon = false }) {
  return (
    <div style={chipS.chip}>
      <div style={chipS.head}>
        <span style={chipS.title}>{title}</span>
        {comingSoon && <span className="d1-coming">Coming soon</span>}
      </div>
      <p style={chipS.body}>{body}</p>
    </div>
  );
}
const chipS = {
  chip:  { padding: '12px 14px', border: `1px solid ${D1.rule}`, borderRadius: 4, background: 'rgba(245,239,230,0.02)' },
  head:  { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' },
  title: { fontSize: 13, fontWeight: 600, color: D1.orange, fontFamily: D1.sans, letterSpacing: '-0.01em' },
  body:  { fontSize: 12.5, color: D1.dim, lineHeight: 1.5, margin: 0 },
};

// ═══════════════════════════════════════════════════════════════════
// NAV
// ═══════════════════════════════════════════════════════════════════

function D1Nav() {
  return (
    <nav style={navS.nav}>
      <a href="#top" style={navS.brand} aria-label="Flash Fluency — home">
        <LogoMark size={34}/>
        <span style={navS.brandWord}>Flash Fluency</span>
      </a>
      <div style={navS.links} className="d1-nav-links">
        <a href="#why"     className="d1-nav-link">Features</a>
        <a href="#path"    className="d1-nav-link">Your Path</a>
        <a href="#pricing" className="d1-nav-link">Pricing</a>
      </div>
    </nav>
  );
}
const navS = {
  nav:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between',
               padding: '24px var(--pad-x)', borderBottom: `1px solid ${D1.rule}`, gap: 24 },
  brand:     { display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: D1.ink, flexShrink: 0 },
  brandWord: { fontFamily: D1.serif, fontSize: 20, fontWeight: 500, letterSpacing: -0.01 },
  links:     { display: 'flex', gap: 32, fontSize: 14, color: D1.dim },
};

// ═══════════════════════════════════════════════════════════════════
// HERO
// ═══════════════════════════════════════════════════════════════════

function D1Hero() {
  return (
    <section style={heroS.section}>
      <div className="d1-deco-ko d1-hero-deco" aria-hidden="true">한국어</div>
      <div className="d1-hero-inner" style={heroS.inner}>
        {isBeta() && (
          <div style={{ marginBottom: 28 }}>
            <span className="d1-chip">
              <span className="d1-chip-dot" />
              Free beta — until May 15
            </span>
          </div>
        )}
        <h1 className="d1-serif d1-hero-h1" style={heroS.h1}>
          The Korean app I wish I'd had.
        </h1>
        <p className="d1-hero-sub" style={heroS.sub}>
          Syllable-level pronunciation feedback. An AI sentence coach that
          grades grammar, vocab, and naturalness. Every sentence calibrated
          one step above your level, reviewed right before you'd forget.
        </p>
        <div className="d1-hero-actions">
          <StoreBadgeApple/>
          <StoreBadgeGoogle/>
          <a href="#why" className="d1-btn-ghost" style={{ padding: '11px 16px', fontSize: 14 }}>
            See how it works <span style={{ color: D1.orange }}>↓</span>
          </a>
        </div>
        <div className="d1-hero-community" style={heroS.community}>
          <span className="d1-eyebrow" style={{ fontSize: 11 }}>Community</span>
          <div style={heroS.socials}>
            <SocialDiscord/><SocialInstagram/><SocialTikTok/>
          </div>
        </div>
      </div>
      <style>{`
        .d1-hero-h1      { font-size: clamp(44px, 8.5vw, 96px); }
        .d1-hero-sub     { font-size: clamp(16px, 1.6vw, 20px); }
        .d1-hero-deco    { position: absolute; top: 60px; right: var(--pad-x); font-size: clamp(120px, 22vw, 280px); }
        .d1-hero-actions { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 28px; }
        @media (max-width: 900px) { .d1-hero-deco { top: 32px; right: var(--pad-x); opacity: 0.7; } }
        @media (max-width: 700px) {
          /* Center the hero on mobile — left-align on a narrow screen
           * leaves big awkward right-side whitespace that looks off. */
          .d1-hero-inner    { text-align: center; }
          .d1-hero-sub      { margin-left: auto !important; margin-right: auto !important; }
          .d1-hero-actions  { justify-content: center; }
          .d1-hero-community { justify-content: center; }
        }
        @media (max-width: 560px) { .d1-hero-deco { top: auto; bottom: 40px; right: 12px; font-size: 160px; opacity: 0.5; } }
      `}</style>
    </section>
  );
}
const heroS = {
  section:   { position: 'relative', padding: 'clamp(48px, 7vw, 80px) var(--pad-x) clamp(56px, 9vw, 96px)', overflow: 'hidden' },
  inner:     { position: 'relative', maxWidth: 820 },
  h1:        { margin: 0, marginBottom: 32, color: D1.ink },
  sub:       { lineHeight: 1.6, color: D1.dim, maxWidth: 580, margin: 0, marginBottom: 40 },
  community: { display: 'flex', alignItems: 'center', gap: 18, marginBottom: 8, flexWrap: 'wrap' },
  socials:   { display: 'flex', alignItems: 'center', gap: 14, opacity: 0.8 },
};

function SocialDiscord() {
  return (
    <a href={SOCIAL.discord} target="_blank" rel="noopener noreferrer" aria-label="Discord" style={{ display: 'inline-flex' }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="#5865F2">
        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
      </svg>
    </a>
  );
}
function SocialInstagram() {
  return (
    <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" style={{ display: 'inline-flex' }}>
      <svg width="18" height="18" viewBox="0 0 24 24">
        <defs>
          <linearGradient id="d1-ig-g" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor="#FFDC80"/>
            <stop offset="25%"  stopColor="#F77737"/>
            <stop offset="50%"  stopColor="#E1306C"/>
            <stop offset="75%"  stopColor="#C13584"/>
            <stop offset="100%" stopColor="#833AB4"/>
          </linearGradient>
        </defs>
        <path fill="url(#d1-ig-g)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    </a>
  );
}
function SocialTikTok() {
  return (
    <a href={SOCIAL.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" style={{ display: 'inline-flex' }}>
      <svg width="18" height="18" viewBox="0 0 24 24">
        <path fill="#25F4EE" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        <path fill="#FE2C55" d="M18.59 5.69a4.83 4.83 0 0 1-3.77-4.25V1h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V8.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 4 19.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        <path fill="#f5efe6" d="M19.09 6.19a4.83 4.83 0 0 1-3.77-4.25V1.5h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V8.9a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 4.5 19.6a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
      </svg>
    </a>
  );
}

// ═══════════════════════════════════════════════════════════════════
// WHY FLASH FLUENCY — 3×2 grid
// ═══════════════════════════════════════════════════════════════════
// Each cell is a self-contained feature card: eyebrow label, small serif
// title (with one orange key word), phone screenshot, chip list. Two
// bottom-row cells are coming-soon teasers at reduced opacity.
//
// Titles use style B: one word or short phrase in orange, rest in ink.
// No italics, no highlighter bands.

const WHY_CELLS = [
  {
    id: 'pronunciation',
    label: 'Pronunciation',
    title: <>Most apps say "perfect!" We tell you the <span className="d1-o">exact sound you missed</span>.</>,
    screenshot: pronScreenshot,
    screenshotAlt: 'Pronunciation feedback — syllable-level comparison',
    chips: [
      { title: 'Per-Sound Feedback',      body: 'Each syllable scored independently. See exactly which sound broke down.' },
      { title: 'Every Sound Change Rule', body: 'Korean has dozens of context-dependent rules. We teach every one.' },
      { title: 'Targeted Practice',       body: 'Drill the rules, vocab, grammar, and syllables you struggle with.' },
    ],
  },
  {
    id: 'output',
    label: 'Output Training',
    title: <>Reading and listening aren't enough. You have to <span className="d1-o">speak it</span>.</>,
    screenshot: outputScreenshot,
    screenshotAlt: 'Translation exercise with Korean sentence construction',
    chips: [
      { title: 'Recall',                body: 'A word is missing from a sentence you know. No word bank — just memory.' },
      { title: 'Sentence Construction', body: 'Build Korean sentences from English prompts, using vocab at your level.' },
      { title: 'Guided Conversation',   body: 'Quick, natural back-and-forth practice.', comingSoon: true },
    ],
  },
  {
    id: 'adaptive',
    label: 'Adaptive Learning Engine',
    title: <>Every session <span className="d1-o">shaped to what you know</span> — and what you don't.</>,
    screenshot: srsScreenshot,
    screenshotAlt: 'Exercise ring interface with reading, listening, pronunciation, and recall',
    chips: [
      { title: 'Deep Proficiency Model',  body: 'Tracked per vocab, grammar, and exercise type — so content is always right-sized.' },
      { title: '+1 Comprehensible Input', body: "Each sentence adds one new thing. Challenged, never overwhelmed." },
      { title: 'Spaced Repetition',       body: "Concepts resurface before you'd forget them." },
    ],
  },
  {
    id: 'frictionless',
    label: 'Frictionless Learning',
    title: <>Don't know a word or grammar? <span className="d1-o">Tap for a lesson.</span></>,
    screenshot: inputScreenshot,
    screenshotAlt: 'Reading exercise with tap-to-reveal vocab and grammar breakdown',
    chips: [
      { title: 'Tap to Reveal',   body: 'Instant breakdown on any word: meaning, components, related grammar, full lesson.' },
      { title: 'Mark for Review', body: "Flag what you don't know. Marked items come back more often." },
    ],
  },
  {
    id: 'content',
    label: 'Your Content, Your Curriculum',
    title: <>K-pop lyrics, journals, chats — <span className="d1-o">become personalized exercises</span>.</>,
    screenshot: personalizationScreenshot,
    screenshotAlt: 'Personalized learning — content-to-curriculum preview',
    chips: [
      { title: 'Import Anything',     body: 'K-pop lyrics, class notes, articles, text conversations.' },
      { title: 'AI-Assisted Writing', body: 'Write in Korean with real-time coaching.' },
      { title: 'Your +1 Curriculum',  body: 'Vocab and grammar extracted into personalized exercises across every type.' },
    ],
    comingSoon: true,
  },
  {
    id: 'conversation',
    label: 'Conversation Practice',
    title: <><span className="d1-o">Real conversations</span> with real feedback.</>,
    // No screenshot yet — future feature
    screenshot: null,
    chips: [
      { title: 'Live Exchanges',   body: 'Quick back-and-forth in Korean, voiced and graded.' },
      { title: 'Scenario Prompts', body: 'Ordering food, giving directions, small talk — practice what you need.' },
      { title: 'Correction Loop',  body: 'Every reply scored on grammar, vocab, and naturalness — fix and try again.' },
    ],
    comingSoon: true,
  },
];

function D1WhyCell({ cell }) {
  return (
    <article
      id={cell.id}
      style={{
        ...whyS.cell,
        opacity: cell.comingSoon ? 0.62 : 1,
      }}
    >
      <div className="d1-why-cell-inner">
        {/* LEFT — eyebrow, title, chips */}
        <div>
          <div style={whyS.labelRow}>
            <div className="d1-eyebrow">{cell.label}</div>
            {cell.comingSoon && <span className="d1-coming">Coming soon</span>}
          </div>

          <h3 className="d1-serif d1-why-title" style={whyS.title}>{cell.title}</h3>

          <div style={whyS.chips}>
            {cell.chips.map((c) => (
              <FeatureChip key={c.title} title={c.title} body={c.body} comingSoon={c.comingSoon}/>
            ))}
          </div>
        </div>

        {/* RIGHT — phone screenshot (or placeholder for future features) */}
        {cell.screenshot ? (
          <div className="d1-phone d1-why-phone">
            <img src={cell.screenshot} alt={cell.screenshotAlt}/>
          </div>
        ) : (
          <div className="d1-why-phone" style={whyS.phonePlaceholder}>
            <div className="d1-ko" style={whyS.phonePlaceholderKo}>대화</div>
            <div style={whyS.phonePlaceholderLabel}>in development</div>
          </div>
        )}
      </div>
    </article>
  );
}

function D1WhyGrid() {
  return (
    <section id="why" style={whyS.section}>
      <div style={whyS.header}>
        <h2 className="d1-serif d1-why-h2" style={whyS.h2}>
          What a serious Korean app looks like.
        </h2>
      </div>

      <div className="d1-why-grid" style={whyS.grid}>
        {WHY_CELLS.map((cell) => <D1WhyCell key={cell.id} cell={cell}/>)}
      </div>

      <style>{`
        .d1-why-h2 { font-size: clamp(32px, 4.5vw, 48px); margin: 0; text-align: center; color: ${D1.ink}; }

        /* 2 columns × 3 rows. Each cell has text on the left and phone on
         * the right, so the cells are wide enough to give the phone breathing
         * room alongside the copy. */
        .d1-why-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1px;
          background: ${D1.rule};
          border: 1px solid ${D1.rule};
        }

        /* Inside each cell, a 1fr + 200px layout gives text the left side
         * and phone the right side. */
        .d1-why-cell-inner {
          display: grid;
          grid-template-columns: 1fr 200px;
          gap: 28px;
          align-items: start;
        }

        .d1-why-title { font-size: clamp(22px, 2vw, 28px); line-height: 1.15; margin: 14px 0 18px; color: ${D1.ink}; }
        .d1-why-phone { width: 200px; aspect-ratio: 280/560; }

        /* Tablet: keep 2 columns but stack text/phone inside each cell. */
        @media (max-width: 1100px) {
          .d1-why-cell-inner { grid-template-columns: 1fr; gap: 18px; }
          .d1-why-phone { width: 100%; max-width: 220px; margin: 0 auto; }
        }
        /* Mobile: 1 column, cells stack vertically. */
        @media (max-width: 700px) {
          .d1-why-grid  { grid-template-columns: 1fr; }
        }
      `}</style>
    </section>
  );
}

const whyS = {
  section: { padding: 'clamp(64px, 9vw, 96px) var(--pad-x)' },
  header:  { marginBottom: 48 },
  h2:      {},
  grid:    {},
  cell: {
    background: D1.bg,
    padding: '28px 26px',
  },
  labelRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minHeight: 20 },
  title:   {},
  chips:   { display: 'grid', gap: 8 },
  phonePlaceholder: {
    borderRadius: 28, border: `2px dashed ${D1.rule}`, background: 'rgba(245,239,230,0.02)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
  },
  phonePlaceholderKo:    { fontSize: 56, color: 'rgba(255,138,61,0.18)', letterSpacing: '-0.05em' },
  phonePlaceholderLabel: { fontSize: 10, fontFamily: D1.mono, color: D1.faint, letterSpacing: '0.1em', textTransform: 'uppercase' },
};

// ═══════════════════════════════════════════════════════════════════
// YOUR PATH
// ═══════════════════════════════════════════════════════════════════

const PATH_STEPS = [
  { n: '01', title: 'Learn Hangul',      body: 'Every syllable natively voiced with pronunciation feedback on every sound.' },
  { n: '02', title: 'Build Foundations', body: 'A guided curriculum that walks you through your first sentences, vocab, and grammar.' },
  { n: '03', title: 'Nail the basics',   body: '5,000 words by frequency. 300+ grammar points. The core that gets you to real conversations.' },
  { n: '04', title: 'Make it yours',     body: 'Topic vocab for what you care about — 2,000 words across 100+ topics. Soon: import your own material — lyrics, journals, chats.' },
];
function D1Path() {
  return (
    <section id="path" style={pathS.section}>
      <div className="d1-path-header" style={pathS.header}>
        <div className="d1-eyebrow">Your path</div>
        <h2 className="d1-serif d1-path-h2" style={pathS.h2}>
          From Hangul to <span className="d1-o">conversations</span>.
        </h2>
      </div>
      <div className="d1-path-steps" style={pathS.steps}>
        {PATH_STEPS.map((s, i) => (
          <div
            key={s.n}
            className="d1-path-cell"
            style={{
              ...pathS.cell,
              borderRight: i === PATH_STEPS.length - 1 ? `1px solid ${D1.rule}` : 'none',
            }}
          >
            <div style={pathS.stepEyebrow}>STEP {s.n}</div>
            <h4 className="d1-serif d1-path-title" style={pathS.title}>{s.title}</h4>
            <p className="d1-path-body" style={pathS.body}>{s.body}</p>
          </div>
        ))}
      </div>
      <style>{`
        .d1-path-header { grid-template-columns: 220px 1fr; }
        .d1-path-h2     { font-size: clamp(28px, 3.4vw, 40px); margin: 0; line-height: 1.1; max-width: 720px; }
        .d1-path-steps  { grid-template-columns: repeat(4, 1fr); }
        .d1-path-title  { font-size: clamp(20px, 2vw, 24px); margin: 0 0 10px; }
        .d1-path-body   { font-size: 14px; color: ${D1.dim}; line-height: 1.6; margin: 0; }

        /* 4 cards is tight on laptop — drop to 2x2 under 1100px. */
        @media (max-width: 1100px) {
          .d1-path-steps  { grid-template-columns: repeat(2, 1fr); }
          /* with 2x2, every cell needs right+bottom borders except the
           * right column (gets no right) and bottom row (gets no bottom).
           * simpler: give everyone right+bottom, then remove on the edges. */
          .d1-path-cell                { border-right: 1px solid ${D1.rule}; border-bottom: 1px solid ${D1.rule}; }
          .d1-path-cell:nth-child(2n)  { border-right: none; }
          .d1-path-cell:nth-child(n+3) { border-bottom: none; }
        }

        @media (max-width: 900px) {
          .d1-path-header { grid-template-columns: 1fr; gap: 16px !important; }
        }

        /* Mobile — stack to single column. */
        @media (max-width: 700px) {
          .d1-path-steps  { grid-template-columns: 1fr; }
          .d1-path-cell,
          .d1-path-cell:nth-child(2n)  { border-right: none; border-left: none !important; border-top: 1px solid ${D1.rule}; padding: 28px 0 !important; }
          .d1-path-cell:nth-child(n+3) { border-bottom: none; }
          .d1-path-cell:last-child     { border-bottom: 1px solid ${D1.rule}; }
        }
      `}</style>
    </section>
  );
}
const pathS = {
  section: { background: D1.bg, borderTop: `1px solid ${D1.rule}`, padding: 'clamp(64px, 9vw, 80px) var(--pad-x)' },
  header:  { display: 'grid', gap: 48, marginBottom: 40 },
  h2:      { color: D1.ink },
  steps:   { display: 'grid', gap: 0 },
  cell:    { padding: '32px 24px', borderLeft: `1px solid ${D1.rule}` },
  stepEyebrow: { fontFamily: D1.mono, fontSize: 12, color: D1.orange, letterSpacing: '0.1em', marginBottom: 20 },
  title:   { color: D1.ink },
  body:    {},
};

// ═══════════════════════════════════════════════════════════════════
// STATS
// ═══════════════════════════════════════════════════════════════════

const STATS = [
  { number: '7,000+', label: 'Vocab Lessons' },
  { number: '300+',   label: 'Grammar Lessons' },
  { number: 'Every',  label: 'Pronunciation Rule' },
  { number: '100+',   label: 'Vocab Topics' },
];
function D1Stats() {
  return (
    <section style={statsS.section}>
      <div style={statsS.eyebrowRow}>
        <span className="d1-eyebrow">What's inside</span>
      </div>
      <div className="d1-stats-grid" style={statsS.grid}>
        {STATS.map((s) => (
          <div key={s.label} style={statsS.cell}>
            <div className="d1-serif d1-stats-num" style={statsS.num}>{s.number}</div>
            <div style={statsS.label}>{s.label}</div>
          </div>
        ))}
      </div>
      <style>{`
        .d1-stats-grid { grid-template-columns: repeat(4, 1fr); }
        .d1-stats-num  { font-size: clamp(36px, 4.5vw, 56px); }
        @media (max-width: 900px) { .d1-stats-grid { grid-template-columns: repeat(2, 1fr); } }
      `}</style>
    </section>
  );
}
const statsS = {
  section:    { padding: 'clamp(56px, 7vw, 80px) var(--pad-x)', background: D1.bg2, borderTop: `1px solid ${D1.rule}` },
  eyebrowRow: { textAlign: 'center', marginBottom: 32 },
  grid:       { display: 'grid', gap: 0 },
  cell:       { padding: '28px 24px', textAlign: 'center' },
  num:        { color: D1.orange, fontWeight: 500, lineHeight: 1, marginBottom: 10, letterSpacing: '-0.02em' },
  label:      { fontFamily: D1.mono, fontSize: 11, color: D1.dim, letterSpacing: '0.1em', textTransform: 'uppercase' },
};

// ═══════════════════════════════════════════════════════════════════
// CTA
// ═══════════════════════════════════════════════════════════════════

function D1CTA() {
  return (
    <section id="download" style={ctaS.section}>
      <div className="d1-ko d1-cta-deco" aria-hidden="true">유창</div>
      <div style={ctaS.inner}>
        <h2 className="d1-serif d1-cta-h2" style={ctaS.h2}>
          Ready when you are.
        </h2>
        <p className="d1-cta-sub" style={ctaS.sub}>Free during beta. No card. iOS and Android.</p>
        <div style={ctaS.buttons}>
          <StoreBadgeApple compact/>
          <StoreBadgeGoogle compact/>
        </div>
      </div>
      <style>{`
        .d1-cta-h2  { font-size: clamp(40px, 6.5vw, 72px); margin: 0 0 20px; }
        .d1-cta-sub { font-size: clamp(15px, 1.4vw, 18px); color: ${D1.dim}; margin: 0 0 36px; }
        .d1-cta-deco {
          position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
          font-size: clamp(160px, 24vw, 280px);
          line-height: 0.8; color: rgba(255,138,61,0.08);
          user-select: none; pointer-events: none;
        }
        @media (max-width: 560px) { .d1-cta-deco { font-size: 180px; opacity: 0.6; } }
      `}</style>
    </section>
  );
}
const ctaS = {
  section: { position: 'relative', padding: 'clamp(80px, 12vw, 120px) var(--pad-x) clamp(72px, 10vw, 100px)',
             textAlign: 'center', borderTop: `1px solid ${D1.rule}`, overflow: 'hidden' },
  inner:   { position: 'relative' },
  h2:      { color: D1.ink },
  sub:     {},
  buttons: { display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' },
};

// ═══════════════════════════════════════════════════════════════════
// FOOTER
// ═══════════════════════════════════════════════════════════════════

function D1Footer() {
  return (
    <footer style={fS.footer}>
      <div className="d1-footer-grid" style={fS.grid}>
        <div style={fS.brandLine}>
          <LogoMark size={24}/>
          <span style={fS.brandWord}>Flash Fluency</span>
          <span style={fS.copy}>© {new Date().getFullYear()} — Built by a Korean learner, for Korean learners.</span>
        </div>
        <div className="d1-footer-links" style={fS.links}>
          <a href="/inside-flash-fluency" style={fS.link}>Journal</a>
          <a href="/support-request"      style={fS.link}>Support</a>
          <a href="/contact"              style={fS.link}>Business</a>
          <a href="/terms_of_service"     style={fS.link}>Terms</a>
          <a href="/privacy_policy"       style={fS.link}>Privacy</a>
          <span style={fS.sep}>·</span>
          <a href={SOCIAL.discord}   target="_blank" rel="noopener noreferrer" style={fS.link}>Discord</a>
          <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" style={fS.link}>Instagram</a>
          <a href={SOCIAL.tiktok}    target="_blank" rel="noopener noreferrer" style={fS.link}>TikTok</a>
        </div>
      </div>
      <style>{`
        .d1-footer-grid  { grid-template-columns: 1fr auto; align-items: center; }
        .d1-footer-links { justify-content: flex-end; }
        @media (max-width: 900px) {
          .d1-footer-grid  { grid-template-columns: 1fr; gap: 20px !important; }
          .d1-footer-links { justify-content: flex-start; }
        }
      `}</style>
    </footer>
  );
}
const fS = {
  footer:    { borderTop: `1px solid ${D1.rule}`, padding: '48px var(--pad-x)', background: D1.bg },
  grid:      { display: 'grid', gap: 32 },
  brandLine: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' },
  brandWord: { fontFamily: D1.serif, fontSize: 16, color: D1.ink },
  copy:      { fontSize: 12, color: D1.faint, marginLeft: 12 },
  links:     { display: 'flex', gap: 20, fontSize: 13, color: D1.dim, flexWrap: 'wrap' },
  link:      { color: D1.dim, textDecoration: 'none' },
  sep:       { color: D1.faint },
};

// ═══════════════════════════════════════════════════════════════════
// PAGE
// ═══════════════════════════════════════════════════════════════════

export default function D1Page() {
  return (
    <div className="d1" id="top">
      <style>{GLOBAL_CSS}</style>

      <D1Nav/>
      <D1Hero/>
      <D1Stats/>
      <D1Path/>
      <D1WhyGrid/>
      <D1Pricing/>
      <D1CTA/>
      <D1Footer/>
    </div>
  );
}